import { Controller } from "@hotwired/stimulus"
export default class extends Controller {
  static targets = ["date", "zone", "slots", "status", "calendar", "submit", "selection", "details", "continue", "summary", "heading", "result", "retry", "loading", "recipientName", "recipientEmail"]
  static values = { url: String, preview: Boolean, initialAt: Number }
  connect() {
    if (!this.hasDateTarget) return
    this.monthCache = new Map()
    const detected = Intl.DateTimeFormat().resolvedOptions().timeZone
    if (!this.initialAtValue && [...this.zoneTarget.options].some(option => option.value === detected)) this.zoneTarget.value = detected
    this.setDateBounds(!this.initialAtValue)
    this.load(null, null, !this.initialAtValue)
  }
  disconnect() { this.abort?.abort() }
  setDateBounds(reset = false) {
    const parts = new Intl.DateTimeFormat("en-US", { timeZone: this.zoneTarget.value, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(new Date())
    const part = name => parts.find(piece => piece.type === name).value
    const today = `${part("year")}-${part("month")}-${part("day")}`
    const end = new Date(`${today}T12:00:00`)
    end.setDate(end.getDate() + 90)
    this.dateTarget.min = today
    this.dateTarget.max = `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, "0")}-${String(end.getDate()).padStart(2, "0")}`
    if (reset || !this.dateTarget.value || this.dateTarget.value < today || this.dateTarget.value > this.dateTarget.max) this.dateTarget.value = today
  }
  async load(event, view, findFirst = false) {
    if (!this.hasDateTarget) return
    if (event && ![this.dateTarget, this.zoneTarget].includes(event.target)) return
    if (event?.target === this.zoneTarget) this.setDateBounds()
    this.findFirst = findFirst
    this.showSelection()
    this.abort?.abort()
    const request = this.abort = new AbortController()
    const selected = new Date(`${this.dateTarget.value}T12:00:00`)
    this.viewMonth = view || new Date(selected.getFullYear(), selected.getMonth(), 1)
    const month = `${this.viewMonth.getFullYear()}-${String(this.viewMonth.getMonth() + 1).padStart(2, "0")}`
    const key = `${this.zoneTarget.value}/${month}`
    const saved = this.monthCache.get(key)
    const reusable = saved && Date.now() - saved.at < 30000
    this.setLoading(!reusable && !this.previewValue)
    this.days = null
    this.renderCalendar(this.viewMonth)
    this.slotsTarget.replaceChildren()
    if (this.hasSubmitTarget) this.submitTarget.disabled = true
    if (this.hasContinueTarget) this.continueTarget.disabled = true
    if (this.hasRetryTarget) this.retryTarget.hidden = true
    this.statusTarget.textContent = "Checking available dates…"
    try {
      let result
      if (reusable) {
        result = saved.result
      } else if (this.previewValue) {
        const days = {}
        const last = new Date(this.viewMonth.getFullYear(), this.viewMonth.getMonth() + 1, 0).getDate()
        for (let day = 1; day <= last; day++) {
          const value = `${month}-${String(day).padStart(2, "0")}`
          const weekday = new Date(`${value}T12:00:00`).getDay()
          days[value] = value >= this.dateTarget.min && value <= this.dateTarget.max && weekday > 0 && weekday < 6 ? ["9:00 AM", "10:30 AM", "2:00 PM"].map((label, index) => ({ value: `preview-${index}`, label })) : []
        }
        result = { days }
      } else {
        const url = new URL(this.urlValue, location.origin)
        url.searchParams.set("date", `${month}-01`)
        url.searchParams.set("zone", this.zoneTarget.value)
        url.searchParams.set("month", "1")
        const response = await fetch(url, { signal: request.signal, headers: { Accept: "application/json" } })
        result = response.headers.get("content-type")?.includes("application/json") ? await response.json() : {}
        if (!response.ok) throw new Error(result.error || "Availability could not be checked.")
        // A routing review is a valid empty result, not calendar availability.
        if (!result.days && !result.message) throw new Error("Availability could not be checked.")
        this.monthCache.set(key, { result, at: Date.now() })
      }
      if (request.signal.aborted) return
      this.days = result.days || {}
      const available = Object.keys(this.days).filter(day => day >= this.dateTarget.min && day <= this.dateTarget.max && this.days[day].length).sort()
      if (findFirst && !available.length && !result.message) {
        const next = new Date(this.viewMonth.getFullYear(), this.viewMonth.getMonth() + 1, 1)
        const nextMonth = `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, "0")}`
        if (nextMonth <= this.dateTarget.max.slice(0, 7)) return this.load(null, next, true)
      }
      let date = this.dateTarget.value
      if (!this.initialAtValue && (findFirst || !this.days[date]?.length)) {
        date = (findFirst ? available[0] : available.find(day => day >= this.dateTarget.value)) || available[0] || `${month}-01`
        this.dateTarget.value = date
      }
      this.renderCalendar(this.viewMonth)
      const slots = this.days[date] || []
      this.statusTarget.textContent = result.message || (this.previewValue ? "Sample times · preview only" : slots.length ? new Date(`${date}T12:00:00`).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" }) : findFirst ? "No times available in the booking window." : "No times available this month. Try the next month.")
      this.renderSlots(slots)
      this.setLoading(false)
    } catch (error) {
      if (error.name !== "AbortError" && !request.signal.aborted) {
        this.setLoading(false)
        this.statusTarget.textContent = error.message
        if (this.hasRetryTarget) this.retryTarget.hidden = false
      }
    }
  }
  setLoading(loading) {
    this.loadingTargets.forEach(target => { target.hidden = !loading })
    this.slotsTarget.hidden = loading
    if (this.hasCalendarTarget) this.calendarTarget.setAttribute("aria-busy", String(loading))
  }
  retry() {
    this.monthCache.clear()
    this.load(null, this.viewMonth, this.findFirst)
  }
  previewSubmit(event) {
    if (this.hasDetailsTarget && this.detailsTarget.hidden) {
      event.preventDefault()
      this.continue()
      return
    }
    if (!this.previewValue) return
    event.preventDefault()
    this.resultTarget.textContent = "Preview complete. No booking was created and no email was sent."
  }
  continue() {
    const selected = this.element.querySelector('input[name="slot"]:checked')
    if (!selected || !this.hasDetailsTarget) return
    const date = new Date(`${this.dateTarget.value}T12:00:00`)
    this.summaryTarget.replaceChildren()
    for (const text of [date.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" }), selected.dataset.label, this.zoneTarget.selectedOptions[0].textContent]) {
      const line = document.createElement("div")
      line.textContent = text
      this.summaryTarget.append(line)
    }
    this.summaryTarget.hidden = false
    this.selectionTarget.hidden = true
    this.detailsTarget.hidden = false
    this.detailsTarget.disabled = false
    this.element.classList.add("is-entering-details")
    this.headingTarget.focus()
    this.element.scrollIntoView({ block: "start" })
  }
  showSelection() {
    if (!this.hasDetailsTarget) return
    this.detailsTarget.hidden = true
    this.detailsTarget.disabled = true
    this.selectionTarget.hidden = false
    this.summaryTarget.hidden = true
    this.element.classList.remove("is-entering-details")
    if (this.hasResultTarget) this.resultTarget.textContent = ""
  }
  updateRecipient(event) {
    if (event.target.name === "intake[name]" && this.hasRecipientNameTarget) this.recipientNameTarget.textContent = event.target.value
    if (event.target.name === "intake[email]" && this.hasRecipientEmailTarget) this.recipientEmailTarget.textContent = event.target.value
  }
  back() {
    this.showSelection()
    this.element.querySelector('input[name="slot"]:checked')?.focus()
  }
  renderSlots(slots) {
    let initialSelected = false
    slots.forEach(slot => {
      const label = document.createElement("label")
      label.className = "scheduling-slot"
      const input = document.createElement("input")
      input.type = "radio"; input.name = "slot"; input.value = slot.value; input.required = true
      input.dataset.label = slot.label + (slot.host ? ` · ${slot.host}` : "")
      input.addEventListener("change", () => {
        if (this.hasContinueTarget) this.continueTarget.disabled = false
        if (this.hasSubmitTarget) this.submitTarget.disabled = false
      })
      const text = document.createElement("span")
      text.textContent = input.dataset.label
      label.append(input, text)
      this.slotsTarget.append(label)
      if (this.hasInitialAtValue && slot.at === this.initialAtValue) {
        input.checked = true
        initialSelected = true
        if (this.hasContinueTarget) this.continueTarget.disabled = false
        if (this.hasSubmitTarget) this.submitTarget.disabled = false
      }
    })
    if (initialSelected) this.continue()
    this.initialAtValue = 0
  }
  renderCalendar(view) {
    if (!this.hasCalendarTarget || !this.dateTarget.value) return
    const selected = new Date(`${this.dateTarget.value}T12:00:00`)
    const month = view || new Date(selected.getFullYear(), selected.getMonth(), 1)
    const header = document.createElement("div")
    header.className = "scheduling-month-header"
    const title = document.createElement("strong")
    title.textContent = month.toLocaleDateString(undefined, { month: "long", year: "numeric" })
    const change = (offset, label) => {
      const button = document.createElement("button")
      button.type = "button"; button.className = "btn btn-sm btn-outline-secondary scheduling-month-arrow"
      const targetMonth = new Date(month.getFullYear(), month.getMonth() + offset, 1)
      const minMonth = this.dateTarget.min.slice(0, 7)
      const maxMonth = this.dateTarget.max.slice(0, 7)
      const monthValue = `${targetMonth.getFullYear()}-${String(targetMonth.getMonth() + 1).padStart(2, "0")}`
      button.disabled = monthValue < minMonth || monthValue > maxMonth
      button.setAttribute("aria-label", label)
      button.textContent = offset < 0 ? "‹" : "›"
      button.addEventListener("click", () => this.load(null, new Date(month.getFullYear(), month.getMonth() + offset, 1)))
      return button
    }
    header.append(change(-1, "Previous month"), title, change(1, "Next month"))
    const grid = document.createElement("div")
    grid.className = "scheduling-month-grid"
    for (const day of ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]) {
      const label = document.createElement("span"); label.textContent = day; label.className = "small text-muted"; grid.append(label)
    }
    for (let index = 0; index < month.getDay(); index++) grid.append(document.createElement("span"))
    const last = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate()
    for (let number = 1; number <= last; number++) {
      const date = new Date(month.getFullYear(), month.getMonth(), number, 12)
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(number).padStart(2, "0")}`
      const button = document.createElement("button")
      button.type = "button"; button.textContent = number
      const available = value >= this.dateTarget.min && value <= this.dateTarget.max && !!this.days?.[value]?.length
      button.className = "btn btn-sm scheduling-day " + (available && value === this.dateTarget.value ? "btn-primary" : "btn-outline-secondary")
      button.disabled = !available
      button.setAttribute("aria-label", date.toLocaleDateString(undefined, { dateStyle: "full" }))
      button.setAttribute("aria-pressed", String(available && value === this.dateTarget.value))
      button.addEventListener("click", () => { this.dateTarget.value = value; this.load() })
      grid.append(button)
    }
    this.calendarTarget.replaceChildren(header, grid)
  }

}
